import { Subject, Observable } from "rxjs";

import { Injectable, OnDestroy, inject } from "@angular/core";
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';

export class SignalrWindow extends Window {
  $: any;
}

export enum ConnectionState {
  Connecting = 1,
  Connected = 2,
  Reconnecting = 3,
  Disconnected = 4
}

export class SignalRConfig {
  url: string;
  hubName: string;
  channel: string;
}

export class Message {
  Id: Number;
  Subject: string;
  IsNew: Boolean
  AppId: string
  Text: string;
  Type: any;
  ObjectId: string;
  Order: Number;

  constructor(private m: any) {
    if (m) {
      this.Id = m.id;
      this.IsNew = (m.status === 0);
      this.AppId = m.message.appId;
      this.Text = m.message.text;
      this.Type = m.message.type;
      this.Subject = m.message.subject;
      this.ObjectId = m.message.objectId;
    }
  }
}

@Injectable()
export class SignalRService implements OnDestroy {
  private window = inject(SignalrWindow);
  private channelConfig = inject<SignalRConfig>("channel.config" as any);
  private readonly config = inject(ConfigService);

  starting$: Observable<any>;
  connectionState$: Observable<ConnectionState>;
  error$: Observable<unknown>;

  private readonly messageSubject = new Subject<Message>();
  private errorSubject = new Subject<any>();
  private startingSubject = new Subject<any>();
  private connectionStateSubject = new Subject<ConnectionState>();

  private hubProxy: any;
  private hubConnection: any;

  // Reconnect handling
  private reconnectAttempts = 0;
  private reconnectTimer: any = null;
  private onlineListener: any;
  private visibilityListener: any;
  private startOptions: any = { transport: ["webSockets", "serverSentEvents", "longPolling"] };
  private active = false;
  private destroyed = false;
  private starting = false;
  private generation = 0;
  private onEvent: ((message: Message) => void) | null = null;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const channelConfig = this.channelConfig;

    const gatewayRoot = requireHostUrl(this.config, 'gateway').replace(/\/api\/?$/u, '');
    channelConfig.url = `${gatewayRoot}/signalr`;
    if (this.window.$ === undefined || this.window.$.hubConnection === undefined) {
      throw new Error("The variable '$' or the .hubConnection() function are not defined...please check the SignalR scripts have been loaded properly");
    }

    this.connectionState$ = this.connectionStateSubject.asObservable();
    this.error$ = this.errorSubject.asObservable();
    this.starting$ = this.startingSubject.asObservable();

    this.hubConnection = this.window.$.hubConnection();
    this.hubConnection.url = channelConfig.url;
    this.hubProxy = this.hubConnection.createHubProxy(channelConfig.hubName);

    this.hubConnection.stateChanged((state: any) => {
      let newState = ConnectionState.Connecting;

      switch (state.newState) {
        case this.window.$.signalR.connectionState.connecting:
          newState = ConnectionState.Connecting;
          break;
        case this.window.$.signalR.connectionState.connected:
          newState = ConnectionState.Connected;
          break;
        case this.window.$.signalR.connectionState.reconnecting:
          newState = ConnectionState.Reconnecting;
          break;
        case this.window.$.signalR.connectionState.disconnected:
          newState = ConnectionState.Disconnected;
          break;
      }

      this.connectionStateSubject.next(newState);
    });

    this.hubConnection.error((error: any) => {
      this.errorSubject.next(error);
    });

    // Handle reconnect lifecycle
    this.hubConnection.reconnecting(() => {
      // Browser throttling/background can cause reconnects
    });

    this.hubConnection.reconnected(() => {
      if (!this.active) return;
      this.clearReconnectTimer();
      this.reconnectAttempts = 0;
      this.subscribeToChannel();
      this.startingSubject.next(null);
    });

    this.hubConnection.disconnected(() => {
      this.scheduleReconnect("disconnected");
    });

    this.registerMessageHandler();

  }

  start(): void {
    if (this.destroyed) return;
    this.active = true;
    this.registerMessageHandler();
    // Avoid duplicate starts when already connecting/connected
    if (this.starting || this.isConnectingOrConnected()) {
      return;
    }
    this.starting = true;
    const generation = this.generation;
    this.hubConnection.start(this.startOptions)
      .done(() => {
        if (!this.active || generation !== this.generation) return;
        this.starting = false;
        this.subscribeToChannel();
        this.startingSubject.next(null);
        this.reconnectAttempts = 0;
        this.clearReconnectTimer();
      })
      .fail(() => {
        if (!this.active || generation !== this.generation) return;
        this.starting = false;
        // Keep the subject alive; schedule reconnect instead of erroring the stream
        this.scheduleReconnect("start-failed");
      });
  }

  /** Logout disables reconnects before stopping the transport (stop emits disconnected). */
  stop(): void {
    const wasActive = this.active;
    this.active = false;
    this.generation++;
    this.starting = false;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    if (this.onEvent) this.hubProxy.off('onEvent', this.onEvent);
    this.onEvent = null;
    if (wasActive || !this.isDisconnected()) this.hubConnection.stop();
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroyed = true;
    this.messageSubject.complete();
    this.startingSubject.complete();
    this.errorSubject.complete();
    this.connectionStateSubject.complete();
  }

  sub(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  publish(ev: Message): void {
    this.hubProxy.invoke("Publish", ev);
  }

  // ----- Private helpers -----
  private registerMessageHandler(): void {
    if (this.onEvent) return;
    const generation = this.generation;
    this.onEvent = (message: Message): void => {
      if (this.active && generation === this.generation) this.messageSubject.next(message);
    };
    this.hubProxy.on('onEvent', this.onEvent);
  }

  private subscribeToChannel(): void {
    if (!this.active) return;
    this.hubProxy.invoke("Subscribe")
      .done(() => {
        // Subscribed to SignalR
      })
      .fail((error: unknown) => {
        this.errorSubject.next(error);
      });
  }

  private isConnectingOrConnected(): boolean {
    const state = this.hubConnection?.state;
    const s = this.window.$?.signalR?.connectionState;
    return s && (state === s.connecting || state === s.connected || state === s.reconnecting);
  }

  private isDisconnected(): boolean {
    const state = this.hubConnection?.state;
    const s = this.window.$?.signalR?.connectionState;
    return s && state === s.disconnected;
  }

  private computeBackoffDelay(): number {
    // 2s, 5s, 10s, 30s, 120s, 120s...
    const steps = [2000, 5000, 10000, 30000, 120000];
    const idx = Math.min(this.reconnectAttempts, steps.length - 1);
    let delay = steps[idx];

    // In background, use longer delay to play nice with throttling
    if (typeof document !== 'undefined' && document.hidden) {
      delay = Math.max(delay, 30000);
    }

    return delay;
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  }

  private scheduleReconnect(reason: string) {
    // Already trying or already connected
    if (!this.active || !this.isDisconnected()) {
      return;
    }

    this.clearReconnectTimer();

    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    const attempt = () => {
      if (!this.active || this.isConnectingOrConnected()) return;
      this.reconnectAttempts++;
      this.start();
    };

    if (!online) {
      // Try once per minute while offline, and trigger immediately when online
      this.reconnectTimer = setTimeout(() => attempt(), 120000);
      this.onlineListener = () => {
        this.clearReconnectTimer();
        attempt();
      };
      window.addEventListener('online', this.onlineListener);
      return;
    }

    const delay = this.computeBackoffDelay();
    this.reconnectTimer = setTimeout(() => attempt(), delay);

    // If tab becomes visible before the timer, try sooner
    this.visibilityListener = () => {
      if (!document.hidden && this.isDisconnected()) {
        this.clearReconnectTimer();
        attempt();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);
  }
}
